import styled from "@emotion/styled";

export const SIDEBAR_WIDTH = "16rem";
export const SIDEBAR_WIDTH_COLLAPSED = "4.25rem";

/** Shared by the collapse control and the page footer so the two bottom bars align. */
export const SHELL_FOOTER_HEIGHT = "3rem";

export const Aside = styled.aside<{
  $collapsed: boolean;
  $mobileOpen: boolean;
}>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 60;
  display: flex;
  flex-direction: column;
  width: ${({ $collapsed }) =>
    $collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH};
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.elevated};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  transition:
    width 0.18s ease,
    transform 0.22s ease;

  /* The gradient reads as a hairline of brand colour down the edge rather than a slab of it. */
  &::after {
    content: "";
    position: absolute;
    inset: 0 0 0 auto;
    width: 2px;
    background: ${({ theme }) => theme.colors.gradient};
    opacity: 0.55;
  }

  @media (max-width: 64rem) {
    width: ${SIDEBAR_WIDTH};
    transform: translateX(
      ${({ $mobileOpen }) => ($mobileOpen ? "0" : "-102%")}
    );
    box-shadow: ${({ $mobileOpen }) =>
      $mobileOpen ? "0 0 40px rgba(0, 0, 0, 0.35)" : "none"};
  }
`;

export const Backdrop = styled.div<{ $open: boolean }>`
  display: none;

  @media (max-width: 64rem) {
    display: ${({ $open }) => ($open ? "block" : "none")};
    position: fixed;
    inset: 0;
    z-index: 55;
    background-color: rgba(0, 0, 0, 0.45);
  }
`;

/** The workspace switcher, pinned above the nav so it is on every page. */
export const WorkspaceCard = styled.button<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: calc(100% - 1.5rem);
  margin: 0 0.75rem 0.85rem;
  padding: ${({ $collapsed }) => ($collapsed ? "0.55rem" : "0.6rem 0.7rem")};
  justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "flex-start")};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  text-align: left;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  .meta {
    flex: 1;
    min-width: 0;
  }

  .name {
    display: block;
    font-size: 0.8125rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .balance {
    display: block;
    font-size: 0.72rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

/**
 * Collapsed, the rail is only 4.25rem wide, so a menu constrained to it wrapped every workspace
 * name down a two-character column. It breaks out to the side instead, at a width set by its own
 * content rather than by the rail.
 */
export const WorkspaceMenu = styled.div<{ $collapsed: boolean }>`
  position: absolute;
  z-index: 20;
  ${({ $collapsed }) =>
    $collapsed
      ? `left: calc(100% - 0.25rem); top: 0; min-width: 13rem; white-space: nowrap;`
      : `left: 0.75rem; right: 0.75rem;`}
  padding: 0.3rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background-color: ${({ theme }) => theme.colors.surface};
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);

  button {
    display: block;
    width: 100%;
    padding: 0.5rem 0.6rem;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background: transparent;
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.8125rem;
    text-align: left;
    cursor: pointer;

    &:hover {
      background-color: ${({ theme }) => theme.colors.background};
    }

    &[aria-current="true"] {
      background: ${({ theme }) => theme.colors.gradient};
      color: ${({ theme }) => theme.colors.primaryForeground};
    }
  }
`;

export const Scroll = styled.nav`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 0.75rem 1rem;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.border};
    border-radius: 4px;
  }
`;

export const SectionLabel = styled.p<{ $collapsed: boolean }>`
  margin: 1.1rem 0 0.4rem 0.6rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  height: ${({ $collapsed }) => ($collapsed ? "0.6rem" : "auto")};
  overflow: hidden;
  transition: opacity 0.15s ease;
`;

export const Separator = styled.hr`
  margin: 0.9rem 0.4rem;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const itemBase = `
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.55rem 0.65rem;
  border: none;
  border-radius: 0.6rem;
  background: transparent;
  font-size: 0.8375rem;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  position: relative;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
`;

export const Item = styled.button<{ $active: boolean; $collapsed: boolean }>`
  ${itemBase}
  justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "flex-start")};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primaryForeground : theme.colors.textMuted};
  background: ${({ $active, theme }) => ($active ? theme.colors.gradient : "transparent")};

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.gradient : theme.colors.background};
    color: ${({ $active, theme }) =>
      $active ? theme.colors.primaryForeground : theme.colors.text};
  }

  svg {
    flex-shrink: 0;
  }

  .label {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: ${({ $collapsed }) => ($collapsed ? "none" : "block")};
  }

  .chevron {
    display: ${({ $collapsed }) => ($collapsed ? "none" : "inline-flex")};
    transition: transform 0.15s ease;
  }
`;

/** A route inside an expanded group. Indented, with a rail instead of an icon. */
export const SubItem = styled.button<{ $active: boolean }>`
  ${itemBase}
  margin-left: 1.15rem;
  width: calc(100% - 1.15rem);
  padding-left: 0.85rem;
  font-size: 0.8125rem;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.textMuted};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.35rem;
    bottom: 0.35rem;
    width: 2px;
    border-radius: 2px;
    background: ${({ $active, theme }) =>
      $active ? theme.colors.gradient : theme.colors.border};
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
  }
`;

export const CollapseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-shrink: 0;
  margin: 0;
  height: ${SHELL_FOOTER_HEIGHT};
  padding: 0 0.7rem;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  font-size: 0.75rem;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    background-color: ${({ theme }) => theme.colors.background};
  }

  @media (max-width: 64rem) {
    display: none;
  }
`;
